import type { TFunction } from 'i18next';

/** MIME types the upload pipeline accepts. Anything else (incl. empty type) is rejected. */
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Hard cap on photos per media unit, enforced client-side and server-side. */
export const MAX_PHOTOS = 10;

interface PartitionPhotoSelectionParams {
  selectedFiles: File[];
  /** Photos already attached (existing + new) that count against the cap. */
  currentCount: number;
  t: TFunction<'inventory'>;
  maxPhotos?: number;
}

interface PartitionPhotoSelectionResult {
  /** Valid files within the remaining cap, ready to append. */
  accepted: File[];
  /**
   * Explicit, persistent message describing every dropped file and why. Combines
   * wrong-type and over-limit reasons so neither clobbers the other. `null` when
   * nothing was dropped.
   */
  error: string | null;
}

function joinNames(files: File[]): string {
  return files.map((f) => f.name).join(', ');
}

/**
 * Splits a selected file batch into accepted files and an explicit rejection
 * message. Files outside the allowed MIME set — including iPhone HEIC and files
 * with an empty/unknown `File.type` — are reported as rejected rather than
 * silently dropped. When the batch also exceeds the photo cap, both reasons are
 * surfaced together.
 */
export function partitionPhotoSelection({
  selectedFiles,
  currentCount,
  t,
  maxPhotos = MAX_PHOTOS,
}: PartitionPhotoSelectionParams): PartitionPhotoSelectionResult {
  const rejectedWrongType: File[] = [];
  const validType: File[] = [];
  for (const file of selectedFiles) {
    if (ALLOWED_PHOTO_TYPES.includes(file.type)) {
      validType.push(file);
    } else {
      rejectedWrongType.push(file);
    }
  }

  const remaining = Math.max(0, maxPhotos - currentCount);
  const accepted = validType.slice(0, remaining);
  const rejectedTooMany = validType.slice(remaining);

  const messages: string[] = [];
  if (rejectedWrongType.length > 0) {
    messages.push(
      t('form.photos.wrongTypeDetailed', {
        count: rejectedWrongType.length,
        names: joinNames(rejectedWrongType),
      }),
    );
  }
  if (rejectedTooMany.length > 0) {
    messages.push(
      t('form.photos.tooManyDetailed', {
        count: rejectedTooMany.length,
        names: joinNames(rejectedTooMany),
      }),
    );
  }

  return { accepted, error: messages.length > 0 ? messages.join(' ') : null };
}
